import React, { useMemo, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { withRouter } from 'react-router-dom';
import { compose } from 'recompose';
import { withSession } from '../../../state/Session';
import { unpackSessionObject } from '../unpackSessionObject';
import config from '../../../config';

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`;

const Container = styled.div`
  max-width: 560px;
  margin: 0 auto;
  padding: 32px 24px;
`;

const Title = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: #fff;
  margin: 0 0 4px;
`;

const Subtitle = styled.p`
  font-size: 0.85rem;
  color: #888;
  margin: 0 0 24px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 0.8rem;
  font-weight: 500;
  color: #aaa;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const Input = styled.input`
  padding: 10px 12px;
  border: 1px solid #333;
  border-radius: 8px;
  background: #1a1a1a;
  color: #e0e0e0;
  font-size: 0.9rem;
  transition: border-color 150ms;

  &:focus {
    outline: none;
    border-color: #00a7f7;
  }
`;

const Select = styled.select`
  padding: 10px 12px;
  border: 1px solid #333;
  border-radius: 8px;
  background: #1a1a1a;
  color: #e0e0e0;
  font-size: 0.9rem;
  transition: border-color 150ms;

  &:focus {
    outline: none;
    border-color: #00a7f7;
  }
`;

const DateRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
  color: #ccc;
  cursor: pointer;
  user-select: none;

  input[type='checkbox'] {
    accent-color: #00a7f7;
    width: 16px;
    height: 16px;
  }
`;

const SubmitButton = styled.button`
  margin-top: 8px;
  padding: 12px;
  border: none;
  border-radius: 8px;
  background: #00a7f7;
  color: #fff;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition:
    background 150ms,
    opacity 150ms;

  &:hover:not(:disabled) {
    background: #0090d6;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid #222;
  margin: 24px 0;
`;

/* ── Status Banner ── */

const StatusBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 10px;
  font-size: 0.9rem;
  font-weight: 500;

  ${({ $variant }) =>
    $variant === 'running' &&
    `
    background: rgba(0, 167, 247, 0.08);
    border: 1px solid rgba(0, 167, 247, 0.25);
    color: #5cc8ff;
  `}

  ${({ $variant }) =>
    $variant === 'success' &&
    `
    background: rgba(0, 180, 80, 0.08);
    border: 1px solid rgba(0, 180, 80, 0.25);
    color: #4ade80;
  `}

  ${({ $variant }) =>
    $variant === 'error' &&
    `
    background: rgba(239, 68, 68, 0.08);
    border: 1px solid rgba(239, 68, 68, 0.25);
    color: #f87171;
  `}
`;

const Spinner = styled.div`
  width: 18px;
  height: 18px;
  border: 2px solid rgba(0, 167, 247, 0.25);
  border-top-color: #00a7f7;
  border-radius: 50%;
  animation: ${spin} 0.7s linear infinite;
  flex-shrink: 0;
`;

const PulsingDot = styled.span`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #5cc8ff;
  animation: ${pulse} 1.5s ease-in-out infinite;
  flex-shrink: 0;
`;

const StatusText = styled.span`
  flex: 1;
`;

const CheckIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
    <circle cx="9" cy="9" r="9" fill="rgba(0,180,80,0.2)" />
    <path
      d="M5.5 9.5L7.5 11.5L12.5 6.5"
      stroke="#4ade80"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ErrorIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
    <circle cx="9" cy="9" r="9" fill="rgba(239,68,68,0.2)" />
    <path d="M6.5 6.5L11.5 11.5M11.5 6.5L6.5 11.5" stroke="#f87171" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

/* ── JSON Toggle ── */

const ToggleButton = styled.button`
  margin-top: 8px;
  padding: 0;
  border: none;
  background: none;
  color: #666;
  font-size: 0.8rem;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;

  &:hover {
    color: #999;
  }
`;

const JsonBox = styled.pre`
  margin-top: 8px;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid #222;
  background: #111;
  color: #aaa;
  font-size: 0.8rem;
  max-height: 360px;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-word;
`;

const ErrorText = styled.div`
  color: #f87171;
  font-size: 0.85rem;
`;

const SeedToolsUI = ({ accessToken, match }) => {
  const scopedOrgId = useMemo(() => {
    const parsed = Number(match?.params?.organizationId);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }, [match]);
  const defaultOrgId = useMemo(() => (scopedOrgId ? String(scopedOrgId) : ''), [scopedOrgId]);

  const [orgIdsInput, setOrgIdsInput] = useState(defaultOrgId);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [path, setPath] = useState('schedule');
  const [mode, setMode] = useState('missing_only');
  const [skipWeekends, setSkipWeekends] = useState(true);
  const [dryRun, setDryRun] = useState(true);
  const [removeManualData, setRemoveManualData] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [jobId, setJobId] = useState('');
  const [jobStatus, setJobStatus] = useState('');
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [showJson, setShowJson] = useState(false);

  const extractJobError = json => {
    if (!json) return '';
    if (json.error) return json.error;

    const pathError = json.results?.flatMap(org => org?.paths || [])?.find(pathResult => pathResult?.error)?.error;

    if (pathError) return pathError;
    if (json.success === false) return 'Seed job completed with errors.';
    return '';
  };

  const parseOrgIds = value =>
    value
      .split(',')
      .map(v => Number(v.trim()))
      .filter(v => Number.isFinite(v) && v > 0);

  const onSubmit = async e => {
    e.preventDefault();
    setError('');
    setResult(null);
    setJobId('');
    setJobStatus('');
    setShowJson(false);

    if (!accessToken) {
      setError('Missing session access token.');
      return;
    }

    const orgIds = scopedOrgId ? [scopedOrgId] : parseOrgIds(orgIdsInput);
    if (orgIds.length === 0) {
      setError('Provide at least one valid org id.');
      return;
    }
    if (!from || !to) {
      setError('Both period from/to are required.');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(`${config.apiURL}/tools/seed?token=${encodeURIComponent(accessToken)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          orgIds,
          paths: [path],
          period: { from, to },
          mode,
          skipWeekends,
          dryRun,
          removeManualData,
        }),
      });

      const json = await response.json();
      if (!response.ok) {
        setError(json?.error || `Request failed (${response.status})`);
        setResult(json);
        return;
      }
      const id = json?.jobId;
      if (!id) {
        setError('Missing jobId in seed response.');
        setResult(json);
        return;
      }
      setJobId(id);
      setJobStatus(json?.status || 'queued');
      setIsPolling(true);
    } catch (err) {
      setError(err?.message || 'Request failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  React.useEffect(() => {
    if (!isPolling || !jobId || !accessToken) return undefined;

    let cancelled = false;
    const poll = async () => {
      try {
        const response = await fetch(
          `${config.apiURL}/tools/seed/status/${encodeURIComponent(jobId)}?token=${encodeURIComponent(accessToken)}`
        );
        const json = await response.json();
        if (cancelled) return;
        if (!response.ok) {
          setError(json?.error || `Status check failed (${response.status})`);
          setIsPolling(false);
          return;
        }

        setJobStatus(json?.status || 'unknown');
        if (json?.status === 'completed' || json?.status === 'failed') {
          setResult(json);
          setIsPolling(false);
          const jobError = extractJobError(json);
          if (json?.status === 'failed' || json?.success === false || jobError) {
            setError(jobError || 'Seed job failed');
          }
        }
      } catch (err) {
        if (!cancelled) {
          setError(err?.message || 'Status check failed');
          setIsPolling(false);
        }
      }
    };

    poll();
    const timer = setInterval(poll, 2000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [isPolling, jobId, accessToken]);

  const isRunning = isSubmitting || isPolling;
  const isDone = !isRunning && jobId && !error;
  const isFailed = !isRunning && jobId && !!error;

  return (
    <Container>
      <Title>Seed Tools</Title>
      <Subtitle>Trigger targeted data seeding</Subtitle>

      <Form onSubmit={onSubmit}>
        {!scopedOrgId && (
          <FieldGroup>
            <Label htmlFor="orgIds">Org IDs</Label>
            <Input
              id="orgIds"
              value={orgIdsInput}
              onChange={e => setOrgIdsInput(e.target.value)}
              placeholder="1, 2, 3"
            />
          </FieldGroup>
        )}

        <DateRow>
          <FieldGroup>
            <Label htmlFor="from">From</Label>
            <Input id="from" type="date" value={from} onChange={e => setFrom(e.target.value)} />
          </FieldGroup>
          <FieldGroup>
            <Label htmlFor="to">To</Label>
            <Input id="to" type="date" value={to} onChange={e => setTo(e.target.value)} />
          </FieldGroup>
        </DateRow>

        <FieldGroup>
          <Label htmlFor="path">Path</Label>
          <Select id="path" value={path} onChange={e => setPath(e.target.value)}>
            <option value="schedule">schedule</option>
          </Select>
        </FieldGroup>

        <FieldGroup>
          <Label htmlFor="mode">Mode</Label>
          <Select id="mode" value={mode} onChange={e => setMode(e.target.value)}>
            <option value="missing_only">missing_only</option>
            <option value="overwrite">overwrite</option>
            <option value="remove">remove</option>
          </Select>
        </FieldGroup>

        <CheckboxLabel>
          <input type="checkbox" checked={skipWeekends} onChange={e => setSkipWeekends(e.target.checked)} />
          Skip weekends
        </CheckboxLabel>

        {path === 'schedule' && (mode === 'overwrite' || mode === 'remove') && (
          <CheckboxLabel>
            <input type="checkbox" checked={removeManualData} onChange={e => setRemoveManualData(e.target.checked)} />
            Also remove manually added data
          </CheckboxLabel>
        )}

        <CheckboxLabel>
          <input type="checkbox" checked={dryRun} onChange={e => setDryRun(e.target.checked)} />
          Dry run
        </CheckboxLabel>

        <SubmitButton type="submit" disabled={isRunning}>
          {isSubmitting ? 'Submitting\u2026' : 'Run Seed'}
        </SubmitButton>
      </Form>

      {(isRunning || isDone || isFailed || (error && !jobId)) && <Divider />}

      {/* In-progress */}
      {isRunning && (
        <StatusBanner $variant="running">
          <Spinner />
          <StatusText>
            {isSubmitting ? (
              'Submitting job\u2026'
            ) : (
              <>
                Seeding in progress &mdash; <em>{jobStatus || 'queued'}</em>
              </>
            )}
          </StatusText>
          {isPolling && <PulsingDot />}
        </StatusBanner>
      )}

      {/* Success */}
      {isDone && (
        <StatusBanner $variant="success">
          <CheckIcon />
          <StatusText>Seed completed successfully</StatusText>
        </StatusBanner>
      )}

      {/* Failed */}
      {isFailed && (
        <StatusBanner $variant="error">
          <ErrorIcon />
          <StatusText>{error}</StatusText>
        </StatusBanner>
      )}

      {/* Validation error (no job) */}
      {error && !jobId && <ErrorText>{error}</ErrorText>}

      {/* JSON toggle */}
      {result && (
        <>
          <ToggleButton type="button" onClick={() => setShowJson(v => !v)}>
            {showJson ? 'Hide response' : 'Show response JSON'}
          </ToggleButton>
          {showJson && <JsonBox>{JSON.stringify(result, null, 2)}</JsonBox>}
        </>
      )}
    </Container>
  );
};

export default compose(withRouter, withSession(unpackSessionObject))(SeedToolsUI);
